"use client";

import {
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonPage,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import type React from "react";
import { useState, useEffect } from "react";
import axios from "axios";

// Material UI
import {
  Box,
  Typography,
  Button,
  Dialog,
  TextField,
  Tabs,
  Tab,
  CircularProgress,
  Autocomplete,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  Fab,
  Stack,
  Badge,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import InboxIcon from "@mui/icons-material/Inbox";
import OutboxIcon from "@mui/icons-material/Outbox";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import EditIcon from "@mui/icons-material/Edit";
import { baseUrl, config, requestID } from "../../../common/utils";

// Styled components for better visual hierarchy
const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  "& .MuiTab-root": {
    minHeight: 64,
    textTransform: "none",
    fontSize: "1rem",
    fontWeight: 500,
  },
}));

const MessageCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  transition: "all 0.2s ease-in-out",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[4],
    borderColor: alpha(theme.palette.primary.main, 0.3),
  },
}));

const ComposeDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.spacing(2),
    minHeight: "500px",
  },
}));

const FloatingCompose = styled(Fab)(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  zIndex: 1000,
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
}));

const MailPage: React.FC = () => {
  // Messages
  const [inbox, setInbox] = useState<any[]>([]);
  const [sent, setSent] = useState<any[]>([]);

  // UI states
  const [tab, setTab] = useState(1); // 0 = Compose, 1 = Inbox, 2 = Sent
  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false); // for send button

  // Compose form states
  const [to, setTo] = useState<any>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [users, setUsers] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [unread, setUnRead] = useState("");

  useEffect(() => {
    loadAllData();
  }, []);

  async function loadAllData() {
    try {
      setPageLoading(true);
      await Promise.all([
        getAllUsers(),
        getAllInbox(),
        getAllMessage(),
        getNumberOfUnread(),
        markRead(),
      ]);
    } catch (error) {
      console.error("Failed to load initial data:", error);
    } finally {
      setPageLoading(false);
    }
  }

  // Get new messages
  async function getNumberOfUnread() {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(
        baseUrl() + "Appmessages/getMessageNewInbox",
        formData,
        config
      );
      setUnRead(response.data.data);
      console.log("unread", response);
    } catch (error) {
      console.error("Failed to load users:", error);
    }
  }

  // Mark read
  async function markRead() {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(
        baseUrl() + "Appmessages/markRead",
        formData,
        config
      );
      console.log("read", response);
    } catch (error) {
      console.error("Failed to load users:", error);
    }
  }

  // Load users (you can later make this endpoint support search & pagination)
  async function getAllUsers(query = "") {
    try {
      setSearching(true);
      const formData = new FormData();
      formData.append("requestID", requestID());
      if (query) formData.append("search", query); // backend should filter

      const response = await axios.post(
        baseUrl() + "businesss/getAllStaff_inbox",
        formData,
        config
      );
        console.log("users list", response || []);
      setUsers(response.data.data || []);
    } catch (error) {
      console.error("Failed to load users:", error);
    } finally {
      setSearching(false);
    }
  }

  // Send message
  async function handleSend() {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("receiverID", to?.user_id || "");
      formData.append("title", subject);
      formData.append("message", body);

      const response = await axios.post(
        baseUrl() + "Appmessages/sendmessage",
        formData,
        config
      );
      console.log("Message sent:", response.data);

      // Refresh inbox/sent
      getAllInbox();
      getAllMessage();

      // Reset form & close popup
      setTo(null);
      setSubject("");
      setBody("");
      setTab(1); // Switch to inbox tab
    } catch (error) {
      console.error("Send failed:", error);
    } finally {
      setLoading(false);
    }
  }

  // Load inbox
  async function getAllInbox() {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      const response = await axios.post(
        baseUrl() + "Appmessages/getMessagebyInbox",
        formData,
        config
      );
      setInbox(response.data.data || []);
      console.log("inbox", response || []);
    } catch (error) {
      console.error("Inbox fetch failed:", error);
    }
  }

  // Load sent
  async function getAllMessage() {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      const response = await axios.post(
        baseUrl() + "Appmessages/getMessagebySent2",
        formData,
        config
      );
      setSent(response.data.data || []);
    } catch (error) {
      console.error("Sent fetch failed:", error);
    }
  }

  return (
    <IonPage className="pushDown">
      {/* Ionic Header */}
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Mailbox</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <Box
          maxWidth="900px"
          mx="auto"
          height="100vh"
          display="flex"
          flexDirection="column"
          sx={{ backgroundColor: "background.default", position: "relative" }}
        >
          {pageLoading && (
            <LoadingOverlay>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Loading your messages...
              </Typography>
            </LoadingOverlay>
          )}

          <Box
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              p: 3,
              borderRadius: "0 0 24px 24px",
              mb: 2,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h4" fontWeight="700" gutterBottom>
                  Mailbox
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Stay connected with your team
                </Typography>
              </Box>
              {Number(unread) > 0 && (
                <Badge badgeContent={unread} color="error">
                  <EmailIcon sx={{ fontSize: 40 }} />
                </Badge>
              )}
            </Stack>
          </Box>

          <StyledTabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            centered
            sx={{ mx: 2, borderRadius: 2, mb: 2 }}
          >
            <Tab icon={<EditIcon />} label="Compose" iconPosition="start" />
            <Tab
              icon={<InboxIcon />}
              label={`Inbox (${inbox.length})`}
              iconPosition="start"
            />
            <Tab
              icon={<OutboxIcon />}
              label={`Sent (${sent.length})`}
              iconPosition="start"
            />
          </StyledTabs>

          {tab === 0 && (
            <Box p={2} flex={1} overflow="auto">
              <Card elevation={0} sx={{ border: 1, borderColor: "divider" }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" fontWeight="600" gutterBottom>
                    Compose Message
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  <Stack spacing={3}>
                    <Autocomplete
                      options={users}
                      getOptionLabel={(option: any) =>
                        `${option.firstName} (${
                          option.email || option.user_id
                        })`
                      }
                      loading={searching}
                      value={to}
                      onChange={(_, value) => setTo(value)}
                      onInputChange={(_, value) => {
                        if (value.length > 1) getAllUsers(value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="To"
                          variant="outlined"
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {searching ? (
                                  <CircularProgress size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Avatar sx={{ mr: 2, bgcolor: "primary.main" }}>
                            <PersonIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body1">
                              {option.firstName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {option.email || option.user_id}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    />

                    <TextField
                      fullWidth
                      label="Subject"
                      variant="outlined"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                    />

                    <TextField
                      fullWidth
                      multiline
                      minRows={8}
                      label="Message"
                      variant="outlined"
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="Type your message here..."
                    />

                    <Box display="flex" justifyContent="flex-end" gap={2}>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => {
                          setTo(null);
                          setSubject("");
                          setBody("");
                        }}
                      >
                        Clear
                      </Button>
                      <Button
                        variant="contained"
                        size="large"
                        endIcon={
                          loading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <SendIcon />
                          )
                        }
                        onClick={handleSend}
                        disabled={loading || !to || !subject || !body}
                        sx={{
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                          },
                        }}
                      >
                        {loading ? "Sending..." : "Send Message"}
                      </Button>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          )}

          {tab === 1 && (
            <Box p={2} flex={1} overflow="auto">
              {inbox.length === 0 ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="300px"
                  sx={{ opacity: 0.6 }}
                >
                  <InboxIcon sx={{ fontSize: 64, mb: 2 }} />
                  <Typography variant="h6">No messages in inbox</Typography>
                  <Typography variant="body2">Your inbox is empty</Typography>
                </Box>
              ) : (
                inbox.map((msg, i) => (
                  <MessageCard key={i} elevation={0}>
                    <CardContent>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="flex-start"
                      >
                        <Avatar sx={{ bgcolor: "primary.main" }}>
                          <PersonIcon />
                        </Avatar>
                        <Box flex={1}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                          >
                            <Typography variant="subtitle1" fontWeight="600">
                              {msg.firstName}
                            </Typography>
                            <Chip
                              label="New"
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Stack>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{ color: "text.primary" }}
                          >
                            {msg.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {msg.message}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </MessageCard>
                ))
              )}
            </Box>
          )}

          {tab === 2 && (
            <Box p={2} flex={1} overflow="auto">
              {sent.length === 0 ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="300px"
                  sx={{ opacity: 0.6 }}
                >
                  <OutboxIcon sx={{ fontSize: 64, mb: 2 }} />
                  <Typography variant="h6">No sent messages</Typography>
                  <Typography variant="body2">
                    You haven't sent any messages yet
                  </Typography>
                </Box>
              ) : (
                sent.map((msg, i) => (
                  <MessageCard key={i} elevation={0}>
                    <CardContent>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="flex-start"
                      >
                        <Avatar sx={{ bgcolor: "secondary.main" }}>
                          <SendIcon />
                        </Avatar>
                        <Box flex={1}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            gutterBottom
                          >
                            To: {msg.firstName}
                          </Typography>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{ color: "text.primary" }}
                          >
                            {msg.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {msg.message}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </MessageCard>
                ))
              )}
            </Box>
          )}

          <FloatingCompose
            color="primary"
            onClick={() => setTab(0)}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
              },
            }}
          >
            <AddIcon />
          </FloatingCompose>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default MailPage;
